import {
  forwardRef,
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import coinCallcenter from '../../assets/mascot/coin-callcenter.png';
import coinDisappointed from '../../assets/mascot/coin-disappointed.png';
import coinEmpty from '../../assets/mascot/coin-empty.png';
import coinError from '../../assets/mascot/coin-error.png';
import coinHappy from '../../assets/mascot/coin-happy.png';
import coinHint from '../../assets/mascot/coin-hint.png';
import coinNeutral from '../../assets/mascot/coin-neutral.png';
import coinSad from '../../assets/mascot/coin-sad.png';
import coinSleeping from '../../assets/mascot/coin-sleeping.png';
import coinSurprise from '../../assets/mascot/coin-surprice.png';
import coinThinking from '../../assets/mascot/coin-thinking.png';
import coinWinner from '../../assets/mascot/coin-winner.png';
import { cn } from '../../utils';

// Vite → string URL; Next.js → StaticImageData
function toUrl(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  return (raw as { src: string }).src;
}

// ─── Reduced motion hook ──────────────────────────────────────────────────────

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type MascotSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/** @deprecated Pass `emotion="empty"` instead. Kept for backwards compatibility. */
export type MascotVariant = 'colored' | 'gray';

/**
 * Emotional states — each maps to a dedicated illustration + haptic animation.
 *
 * | Emotion       | Image            | Animation         | When to use                        |
 * |---------------|------------------|-------------------|------------------------------------|
 * | neutral       | coin-neutral     | none              | Default, waiting, welcome          |
 * | happy         | coin-happy       | soft bounce ×2    | Success, first action              |
 * | hint          | coin-hint        | wiggle point      | Tips, contextual guides, tour      |
 * | thinking      | coin-thinking    | slow sway         | Loading, processing, FAQ           |
 * | celebrating   | coin-winner      | big bounce ×3     | Achievements, goal reached         |
 * | sleeping      | coin-sleeping    | slow fade-pulse   | Idle, long inactivity              |
 * | surprised     | coin-surprice    | pop + settle      | First time in section, onboarding  |
 * | sad           | coin-sad         | slow droop        | Soft error, cancelled              |
 * | disappointed  | coin-disappointed| head-shake        | Rejected, failed validation        |
 * | error         | coin-error       | hard shake        | Critical failure                   |
 * | empty         | coin-empty       | none              | Empty states (greyscale coin)      |
 * | callcenter    | coin-callcenter  | gentle wave       | Help chat agent, support           |
 */
export type MascotEmotion =
  | 'neutral'
  | 'happy'
  | 'hint'
  | 'thinking'
  | 'celebrating'
  | 'sleeping'
  | 'surprised'
  | 'sad'
  | 'disappointed'
  | 'error'
  | 'empty'
  | 'callcenter';

export interface MascotProps extends Omit<ComponentPropsWithoutRef<'img'>, 'children' | 'src'> {
  size?: MascotSize;
  emotion?: MascotEmotion;
  /** @deprecated Use `emotion="empty"` for greyscale. Kept for backwards compatibility. */
  variant?: MascotVariant;
  /**
   * Enable emotion-specific animation. Defaults to true.
   * `prefers-reduced-motion` is respected automatically regardless of this prop.
   */
  animated?: boolean;
}

// ─── Size map ─────────────────────────────────────────────────────────────────

const sizePx: Record<MascotSize, number> = {
  xs: 24,
  sm: 40,
  md: 72,
  lg: 96,
  xl: 128,
  '2xl': 192,
};

// ─── Emotion → image ──────────────────────────────────────────────────────────

const emotionSrc: Record<MascotEmotion, string> = {
  neutral: toUrl(coinNeutral),
  happy: toUrl(coinHappy),
  hint: toUrl(coinHint),
  thinking: toUrl(coinThinking),
  celebrating: toUrl(coinWinner),
  sleeping: toUrl(coinSleeping),
  surprised: toUrl(coinSurprise),
  sad: toUrl(coinSad),
  disappointed: toUrl(coinDisappointed),
  error: toUrl(coinError),
  empty: toUrl(coinEmpty),
  callcenter: toUrl(coinCallcenter),
};

// ─── Keyframes ───────────────────────────────────────────────────────────────
//
// Each keyframe is tuned to the character's body language in the illustration:
//   celebrating → big vertical bounces (winner on trophy goes high)
//   happy       → soft double-bounce (arms open, welcoming)
//   hint        → wiggle toward the pointing direction
//   thinking    → slow pendulum sway
//   sleeping    → very slow opacity pulse (Zzz breathing)
//   surprised   → quick scale pop then settle
//   sad         → slow droop (scale down slightly, slight tilt)
//   disappointed→ horizontal head-shake
//   error       → rapid hard shake
//   callcenter  → gentle wave side-to-side

const KEYFRAMES = `
@keyframes mascot-celebrate {
  0%          { transform: translateY(0)     scale(1);    }
  18%         { transform: translateY(-18px) scale(1.08); }
  34%         { transform: translateY(-4px)  scale(1.02); }
  50%         { transform: translateY(-14px) scale(1.06); }
  66%         { transform: translateY(-2px)  scale(1.01); }
  82%         { transform: translateY(-10px) scale(1.04); }
  100%        { transform: translateY(0)     scale(1);    }
}
@keyframes mascot-happy {
  0%, 100%    { transform: translateY(0); }
  20%         { transform: translateY(-12px); }
  40%         { transform: translateY(-2px); }
  60%         { transform: translateY(-8px); }
  80%         { transform: translateY(0); }
}
@keyframes mascot-hint {
  0%          { transform: rotate(0deg) translateX(0); }
  15%         { transform: rotate(-10deg) translateX(-4px); }
  35%         { transform: rotate(12deg) translateX(6px); }
  55%         { transform: rotate(-6deg) translateX(-2px); }
  75%         { transform: rotate(6deg) translateX(3px); }
  100%        { transform: rotate(0deg) translateX(0); }
}
@keyframes mascot-sway {
  0%, 100%    { transform: rotate(-6deg); }
  50%         { transform: rotate(6deg); }
}
@keyframes mascot-sleeping {
  0%, 100%    { opacity: 0.8; transform: scale(1); }
  50%         { opacity: 0.45; transform: scale(0.97); }
}
@keyframes mascot-surprised {
  0%          { transform: scale(1); }
  25%         { transform: scale(1.18); }
  50%         { transform: scale(0.94); }
  75%         { transform: scale(1.06); }
  100%        { transform: scale(1); }
}
@keyframes mascot-droop {
  0%          { transform: translateY(0) rotate(0deg); }
  30%         { transform: translateY(4px) rotate(-3deg); }
  60%         { transform: translateY(6px) rotate(-5deg); }
  100%        { transform: translateY(6px) rotate(-5deg); opacity: 0.8; }
}
@keyframes mascot-shake {
  0%, 100%    { transform: translateX(0); }
  15%         { transform: translateX(-8px); }
  30%         { transform: translateX(8px); }
  45%         { transform: translateX(-6px); }
  60%         { transform: translateX(6px); }
  75%         { transform: translateX(-3px); }
  90%         { transform: translateX(3px); }
}
@keyframes mascot-wave {
  0%, 100%    { transform: rotate(0deg); }
  25%         { transform: rotate(-8deg); }
  75%         { transform: rotate(8deg); }
}
`;

let keyframesInjected = false;
function ensureKeyframes() {
  if (keyframesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
  keyframesInjected = true;
}

// Animation CSS per emotion: [class, whether it loops]
const emotionAnim: Partial<Record<MascotEmotion, string>> = {
  celebrating: '[animation:mascot-celebrate_1s_cubic-bezier(0.34,1.56,0.64,1)_infinite]',
  happy: '[animation:mascot-happy_0.9s_ease-in-out_2]',
  hint: '[animation:mascot-hint_0.8s_ease-in-out_2]',
  thinking: '[animation:mascot-sway_2.4s_ease-in-out_infinite]',
  sleeping: '[animation:mascot-sleeping_3s_ease-in-out_infinite]',
  surprised: '[animation:mascot-surprised_0.5s_cubic-bezier(0.34,1.56,0.64,1)_forwards]',
  sad: '[animation:mascot-droop_0.8s_ease-out_forwards]',
  disappointed: '[animation:mascot-shake_0.6s_ease-in-out_1]',
  error: '[animation:mascot-shake_0.4s_ease-in-out_2]',
  callcenter: '[animation:mascot-wave_2s_ease-in-out_infinite]',
};

// ─── Mascot ───────────────────────────────────────────────────────────────────

export const Mascot = forwardRef<HTMLImageElement, MascotProps>(
  (
    {
      size = 'md',
      emotion = 'neutral',
      variant,
      animated = true,
      className,
      alt = '',
      style,
      ...rest
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const side = sizePx[size];

    // Never animate when reduced-motion is set — respect system accessibility preference
    const shouldAnimate = animated && !reducedMotion;
    const animClass = shouldAnimate ? emotionAnim[emotion] : undefined;

    if (shouldAnimate && animClass) ensureKeyframes();

    return (
      <img
        ref={ref}
        src={emotionSrc[emotion]}
        alt={alt}
        aria-hidden={alt === '' ? true : undefined}
        loading="lazy"
        decoding="async"
        className={cn(
          'inline-block object-contain',
          variant === 'gray' && 'grayscale opacity-60',
          animClass,
          className
        )}
        style={{ width: side, height: side, ...style }}
        {...rest}
      />
    );
  }
);
Mascot.displayName = 'Mascot';

// ─── MascotBubble ────────────────────────────────────────────────────────────

export type MascotBubbleDirection = 'right' | 'left' | 'top';

export interface MascotBubbleProps {
  emotion?: MascotEmotion;
  size?: MascotSize;
  direction?: MascotBubbleDirection;
  animated?: boolean;
  showPointer?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Mascot + speech bubble. `animated` defaults true; reduced-motion respected automatically.
 *
 * ```tsx
 * <MascotBubble emotion="celebrating" size="md">
 *   ¡Logro desbloqueado!
 * </MascotBubble>
 * ```
 */
export function MascotBubble({
  emotion = 'neutral',
  size = 'md',
  direction = 'right',
  animated = true,
  showPointer = true,
  className,
  children,
}: MascotBubbleProps) {
  const isVertical = direction === 'top';

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        isVertical && 'flex-col items-start',
        direction === 'left' && 'flex-row-reverse',
        className
      )}
    >
      <Mascot emotion={emotion} size={size} animated={animated} className="shrink-0" />

      <div className="relative">
        {showPointer && direction === 'right' && (
          <>
            <span
              aria-hidden
              className="absolute -left-[9px] top-3.5 h-0 w-0 border-y-[7px] border-r-[9px] border-y-transparent border-r-tkf-line"
            />
            <span
              aria-hidden
              className="absolute -left-[7px] top-[15px] h-0 w-0 border-y-[6px] border-r-[8px] border-y-transparent border-r-tkf-surface"
            />
          </>
        )}
        {showPointer && direction === 'left' && (
          <>
            <span
              aria-hidden
              className="absolute -right-[9px] top-3.5 h-0 w-0 border-y-[7px] border-l-[9px] border-y-transparent border-l-tkf-line"
            />
            <span
              aria-hidden
              className="absolute -right-[7px] top-[15px] h-0 w-0 border-y-[6px] border-l-[8px] border-y-transparent border-l-tkf-surface"
            />
          </>
        )}
        {showPointer && direction === 'top' && (
          <>
            <span
              aria-hidden
              className="absolute -top-[9px] left-4 h-0 w-0 border-x-[7px] border-b-[9px] border-x-transparent border-b-tkf-line"
            />
            <span
              aria-hidden
              className="absolute -top-[7px] left-[17px] h-0 w-0 border-x-[6px] border-b-[8px] border-x-transparent border-b-tkf-surface"
            />
          </>
        )}
        <div className="rounded-2xl border border-tkf-line bg-tkf-surface px-5 py-4 text-sm leading-relaxed text-tkf-fg shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}
